import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Heart, 
  Shield, 
  Sparkles,
  Smile,
  X,
  MessageSquare,
  ArrowRight,
  Brain
} from 'lucide-react';

// 二维码弹窗组件
const QRCodeModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const qrCodes = [
    { name: '微信公众号', icon: MessageSquare, img: '/images/qrcodes/wechat.jpg' },
    { name: '小红书', icon: Heart, img: '/images/qrcodes/xiaohongshu.jpg' }
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 animate-scale-in" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">与我们联系</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-all hover:rotate-90">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-6">
          {qrCodes.map((item) => (
            <div key={item.name} className="text-center group">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 mb-3 group-hover:shadow-lg transition-all duration-300">
                <img 
                  src={item.img} 
                  alt={item.name} 
                  className="w-full aspect-square object-contain rounded-xl"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden items-center justify-center h-24 text-gray-400 text-xs">
                  <item.icon className="w-8 h-8 mb-2" />
                  {item.name}
                </div>
              </div>
              {item.name === '小红书' ? (
                <img 
                  src="/images/qrcodes/xiaohongshu_text.jpg" 
                  alt="小红书" 
                  className="h-6 mx-auto object-contain"
                />
              ) : (
                <p className="text-sm font-medium text-gray-700">{item.name}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// 浮动装饰形状
const FloatingShapes = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* 大圆形渐变 */}
    <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-landing-200/40 to-transparent blur-3xl animate-pulse-slow" />
    <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-gradient-to-tr from-secondary-200/30 to-transparent blur-3xl animate-pulse-slow delay-1000" />
    
    {/* 浮动小球 */}
    <div className="absolute top-1/4 left-[15%] w-4 h-4 rounded-full bg-landing-400/60 animate-float" />
    <div className="absolute top-1/3 right-[20%] w-6 h-6 rounded-full bg-secondary-400/40 animate-float delay-500" />
    <div className="absolute bottom-1/3 left-[25%] w-3 h-3 rounded-full bg-gold-400/50 animate-float delay-1000" />
    <div className="absolute top-[60%] right-[15%] w-5 h-5 rounded-full bg-landing-300/40 animate-float delay-1500" />
    
    {/* 装饰线条 */}
    <svg className="absolute top-20 left-10 w-32 h-32 text-landing-200/30 animate-spin-slow" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="0.5" strokeDasharray="5 5" />
    </svg>
    <svg className="absolute bottom-20 right-10 w-40 h-40 text-secondary-200/30 animate-spin-slow-reverse" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="0.5" strokeDasharray="8 4" />
    </svg>
  </div>
);

// 带气泡提示的使命链接组件
const MissionTooltip = ({ icon: Icon, label, description, colorClass, iconColor, arrowColor }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div 
      className="relative flex items-center space-x-2 text-gray-500 hover:text-landing-600 transition-all duration-300 group cursor-pointer"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      <Icon className={`w-4 h-4 ${iconColor} group-hover:scale-110 transition-transform duration-300`} />
      <span className="text-sm font-medium">{label}</span>
      
      {isVisible && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-4 z-50 animate-fade-in-up">
          <div className={`${colorClass} text-white text-sm rounded-2xl py-4 px-6 shadow-2xl min-w-[280px] max-w-[320px] text-center leading-relaxed backdrop-blur-sm bg-opacity-95`}>
            {description}
          </div>
          <div className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-[-1px] w-0 h-0 border-l-[10px] border-r-[10px] border-t-[10px] border-l-transparent border-r-transparent ${arrowColor}`}></div>
        </div>
      )}
    </div>
  );
};

// 主按钮组件
const PrimaryButton = ({ onClick, children }) => (
  <button 
    onClick={onClick}
    className="group relative px-12 py-5 bg-gradient-to-r from-landing-500 to-landing-600 text-white text-xl font-semibold rounded-2xl shadow-xl shadow-landing-500/30 hover:shadow-2xl hover:shadow-landing-500/40 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
  >
    {/* 光泽效果 */}
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
    
    <span className="relative flex items-center gap-3">
      {children}
      <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
    </span>
  </button>
);

const LandingPage = () => {
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleEnter = () => {
    navigate('/home');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-white to-cream-100 flex flex-col overflow-hidden">
      {/* 浮动装饰 */}
      <FloatingShapes />
      
      {/* 淡化背景 LOGO */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage: 'url(/images/logo-bg.png)',
          backgroundSize: 'min(110vw, 130vh)',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      
      {/* 导航栏 */}
      <nav className={`relative z-10 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <div className="flex items-center space-x-3 group cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="relative">
                <img src="/images/logo.png" alt="PsychBond" className="h-12 w-auto drop-shadow-sm group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute -inset-2 bg-landing-400/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-landing-600 via-landing-500 to-gold-500 bg-clip-text text-transparent">
                PsychBond
              </span>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero 区域 */}
      <section className="flex-1 flex items-center justify-center relative z-10 px-4 py-12">
        <div className="max-w-4xl mx-auto text-center">
          {/* 主标题 */}
          <h1 className={`text-6xl md:text-7xl lg:text-8xl font-bold mb-6 transition-all duration-700 delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
              在觉知之处
            </span>
            <br />
            <span className="bg-gradient-to-r from-landing-500 via-landing-400 to-secondary-400 bg-clip-text text-transparent">
              产生
            </span>
          </h1>
          
          {/* 副标题 */}
          <p className={`text-xl md:text-2xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed font-light transition-all duration-700 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            一个开放的、全年龄段的心理支持平台
            <br />
            融合 CBT、DBT、正念等循证方法
          </p>
          
          {/* 主按钮 */}
          <div className={`transition-all duration-700 delay-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <PrimaryButton onClick={handleEnter}>
              开始探索
            </PrimaryButton>
          </div>
          

        </div>
      </section>

      {/* Footer */}
      <footer className={`relative z-10 bg-white/60 backdrop-blur-md border-t border-cream-200 py-6 transition-all duration-700 delay-700 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            {/* 左侧 */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <img src="/images/logo.png" alt="PsychBond" className="h-8 w-auto opacity-80" />
                <span className="text-lg font-bold text-gray-700">PsychBond</span>
              </div>
              <div className="hidden sm:flex items-center space-x-4 text-sm">
                <button 
                  onClick={() => setIsQRModalOpen(true)}
                  className="text-gray-500 hover:text-landing-600 transition-colors"
                >
                  联系我们
                </button>
              </div>
            </div>
            
            {/* 中间：使命链接 */}
            <div className="flex flex-wrap items-center justify-center gap-4">
              <MissionTooltip 
                icon={Heart}
                label="温暖陪伴"
                description="用专业和温度，陪伴每一个青少年度过成长的每个阶段"
                colorClass="bg-gradient-to-br from-landing-500 to-landing-600"
                iconColor="text-landing-500"
                arrowColor="border-t-landing-500"
              />
              <span className="text-gray-300">|</span>
              <MissionTooltip 
                icon={Shield}
                label="隐私保护"
                description="严格保护用户隐私，为青少年创造安全的倾诉和成长空间"
                colorClass="bg-gradient-to-br from-gold-500 to-amber-500"
                iconColor="text-gold-500"
                arrowColor="border-t-gold-500"
              />
              <span className="text-gray-300">|</span>
              <MissionTooltip 
                icon={Sparkles}
                label="科学专业"
                description="基于 CBT、DBT、正念等循证方法，由持证心理咨询师团队精心设计"
                colorClass="bg-gradient-to-br from-secondary-500 to-purple-500"
                iconColor="text-secondary-500"
                arrowColor="border-t-secondary-500"
              />
            </div>
            
            {/* 右侧 */}
            <div className="text-center lg:text-right text-xs text-gray-400">
              <p>Copyright © 2026 心灵纽带（深圳）科技有限责任公司</p>
              <p className="mt-1">
                <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 transition-colors">粤ICP备2026031266号-1</a>
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* 二维码弹窗 */}
      <QRCodeModal isOpen={isQRModalOpen} onClose={() => setIsQRModalOpen(false)} />

      {/* 全局样式 */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translate(-50%, 10px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(-10px) translateX(-10px); }
          75% { transform: translateY(-30px) translateX(5px); }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.05); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spin-slow-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
        .animate-spin-slow-reverse {
          animation: spin-slow-reverse 25s linear infinite;
        }
        .delay-500 {
          animation-delay: 0.5s;
        }
        .delay-1000 {
          animation-delay: 1s;
        }
        .delay-1500 {
          animation-delay: 1.5s;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
